<template>
    <div class="patient-profile">
        <!-- 📋 COMPONENTES BASE (Medicina General y también visibles en Ginecología) -->
        <template v-if="showGeneralLikeSections">
            <div id="section-hereditarios">
                <Diseases :key="patientRecord.id" :patient-record="patientRecord" />
            </div>
            <div id="section-no-patologicos">
                <Nopathological :key="patientRecord.id" :patient-record="patientRecord" :data="data.non_pathological" />
            </div>
            <div id="section-patologicos">
                <Pathological :key="patientRecord.id" :patient-record="patientRecord" :data="data.pathological" />
            </div>

            <!-- 🏥 Ginecología y Obstetricia - justo después de Antecedentes Patológicos -->
            <template v-if="hasSpecialty(2) && isSpecialtyActive(2) && isPacienteFemenino">
                <div id="section-ginecologia">
                    <Gynecology :patient-record="patientRecord" :data="data.gynecology" />
                </div>
                <div id="section-control-embarazo">
                    <Pregnancycontrol :patient-record="patientRecord" :data="data.gynecology" />
                </div>
            </template>

            <div id="section-padecimiento">
                <Currentcondition
:key="patientRecord.id" :patient-record="patientRecord"
                    :data="data.current_condition" />
            </div>
            <div id="section-exploracion">
                <PhysicalExamination
:key="patientRecord.id" :patient-record="patientRecord"
                    :data="data.physical_examinations" />
            </div>
            <div id="section-diagnosticos">
                <CIE10HC :patient-record="patientRecord" :data="data.diagnostics" />
            </div>
        </template>

        <!-- Pediatría (id: 3) -->
        <PatientPediatria
v-if="hasSpecialty(3) && isSpecialtyActive(3)" :key="`pediatria-${patientRecord.id}`"
            :patient-record="patientRecord" />

        <!-- Oftalmología (id: 4) -->
        <PatientOF
v-if="hasSpecialty(4) && isSpecialtyActive(4)" :key="`oftalmologia-${patientRecord.id}`"
            :patient-record="patientRecord" />

        <!-- Odontología (id: 7) -->
        <PatientDentistry
v-if="hasSpecialty(7) && isSpecialtyActive(7)" :key="`odontologia-${patientRecord.id}`"
            :patient-record="patientRecord" />
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import api from '@/services/api';

import Diseases from './Diseases.vue';
import Nopathological from './Nopathological.vue';
import Pathological from './Pathological.vue';
import Currentcondition from './Currentcondition.vue';
import PhysicalExamination from './PhysicalExamination.vue';
import CIE10HC from './CIE10HC.vue';

import Pregnancycontrol from './Pregnancycontrol.vue';
import Gynecology from './Gynecology.vue';
import PatientPediatria from './PatientPediatria.vue';
import PatientOF from './PatientOF.vue';
import PatientDentistry from './PatientDentistry.vue';

const store = useStore();

const props = defineProps({
    patientRecord: {
        type: Object,
        required: true
    },
    activeSpecialtyId: {
        type: [Number, String, null],
        default: null
    }
});

const data = ref({
    nopathological: null,
    pathological: null,
    currentcondition: null,
    diagnostics: null,
    physical_examinations: null,
    gynecology: null
});

const user = computed(() => store.state.auth.user);
const doctorSpecialties = computed(() => user.value?.doctor?.specialties || []);
const showGeneralLikeSections = computed(() => {
    // Mostrar secciones base tanto para Medicina General como para Ginecología (cuando esa especialidad esté activa)
    const generalActive = hasSpecialty(1) && isSpecialtyActive(1);
    const gyneActive = hasSpecialty(2) && isSpecialtyActive(2);
    return generalActive || gyneActive;
});

const isPacienteFemenino = computed(() => {
    const gender = props.patientRecord?.gender;
    const esFemenino = gender === 'F' || gender === 'female' || gender === 'Femenino' || gender === 'Mujer';
    console.log('👤 GÉNERO DEL PACIENTE:', {
        raw: gender,
        tipo: typeof gender,
        esFemenino: esFemenino,
        pacienteCompleto: props.patientRecord
    });
    return esFemenino;
});

// 🔍 Función para verificar si el médico tiene una especialidad específica por ID
const hasSpecialty = (specialtyId) => {
    if (!user.value) {
        console.warn('⚠️ Usuario no encontrado en store');
        return false;
    }

    if (user.value.doctor && Array.isArray(user.value.doctor.specialties)) {
        return user.value.doctor.specialties.some(s => Number(s.id) === Number(specialtyId));
    }

    return false;
};

const isSpecialtyActive = (id) => {
    const hasSpec = doctorSpecialties.value.some(s => Number(s.id) === Number(id));
    if (!hasSpec) return false;
    if (!props.activeSpecialtyId) return true;
    return Number(props.activeSpecialtyId) === Number(id);
};

const loadData = async () => {
    try {
        const response = await api.get(`/pacientes/${props.patientRecord.id}`);
        data.value = response.data.data;
    } catch (error) {
        console.error('Error al cargar los datos del paciente:', error);
    }
};

onMounted(() => {
    loadData();
});

watch(() => props.patientRecord.id, (newId, oldId) => {
    if (newId && newId !== oldId) {
        loadData();
    }
}, { immediate: false });

watch(() => props.patientRecord?.gender, (newGender, oldGender) => {
    if (newGender !== oldGender) {
        loadData();
    }
}, { immediate: false });

</script>
<style setup>
.patient-profile {
    background-color: #fff;
    border-radius: 8px;
    padding: 0px;
}
</style>
